import {
  HttpErrorResponse,
  type HttpEvent,
  type HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { type Observable, retry, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { MatDialog } from '@angular/material/dialog';
import { Logger } from '@app/utils';

// *** CONSTANTS ***
const logger = Logger.get('ErrorHandling');

// delay in ms
const RETRY_STRATEGY = { count: 1, delay: 500 };

// *** MODELS ***
export interface ErrorHandlerContext {
  request: HttpRequest<unknown>;
  next: HttpHandlerFn;
  authService: AuthService;
  router: Router;
  matDialogService: MatDialog;
  errorType?: string;
}

export type ErrorHandler = (
  error: HttpErrorResponse,
  context: ErrorHandlerContext
) => Observable<HttpEvent<unknown>>;

export const selectErrorHandler = (statusCode: number): ErrorHandler =>
  errorHandlers[statusCode] || handleError();

export const handleError = (
  errorType: string = 'Unhandled Error'
): ErrorHandler => {
  return (error: HttpErrorResponse, context: ErrorHandlerContext) => {
    const { next, request } = context;

    // Check if this is a 500 error wrapping a 422
    if (error.status === 500 && error.error?.error?.includes('Status: 422')) {
      logger.warn(`Validation Error (422): ${error.error.error}`);
      return throwError(() => error);
    }

    switch (errorType) {
      // Handles server errors with an aggressive retry strategy
      case 'server':
        logger.error(`Server Error (${error.status}): ${error.message}`);
        return next(request).pipe(
          retry(RETRY_STRATEGY),
          catchError(() => throwError(() => error))
        );

      default:
        logger.error(`${errorType} (${error.status}): ${error.message}`);
        return throwError(() => error);
    }
  };
};

function handleUnauthorizedError(
  error: HttpErrorResponse,
  context: {
    request: HttpRequest<unknown>,
    authService: AuthService,
    router: Router,
    matDialogService: MatDialog,
    next: HttpHandlerFn
  }
) {
  const {
    request,
    authService,
    router,
    matDialogService,
    next
  } = context;

  // Identify request types for specialized handling
  const isLoginRequest = request.url.includes('/api/v2/login');
  const isInitializeAppRequest = request.url.includes('/api/v2/initialize-app');
  const isAuthenticateUserRequest = request.url.includes('/api/v2/authenticate-user');
  const isRefreshTokenRequest = request.url.includes('/api/v2/refresh-token');

  // Case 1: Login request failed - pass through 
  if (isLoginRequest) {
    return throwError(() => error);
  }

  // Case 2: Initialize app request failed - pass through 
  if (isInitializeAppRequest) {
    logger.debug('Initialize app request failed, continuing normally');
    return throwError(() => error);
  }

  // Case 3: Authenticate user request failed - pass through 
  if (isAuthenticateUserRequest) {
    logger.debug('Authenticate user request failed, continuing normally');
    return throwError(() => error);
  }

  // Case 4: Refresh token request failed - force logout
  if (isRefreshTokenRequest) {
    return handleAuthFailure(error, { authService, router, matDialogService });
  }

  // Case 5: Regular API request - try refreshing token (but only once per request)
  logger.info('Token expired. Attempting to refresh...');

  return authService.refreshToken().pipe(
    switchMap(() => {
      // Retry the original request
      return next(request);
    }),
    catchError(refreshError => {
      return handleAuthFailure(refreshError, { authService, router, matDialogService });
    })
  );
}

// Handles authentication failures by logging out and redirecting
function handleAuthFailure(
  error: unknown,
  context: {
    authService: AuthService,
    router: Router,
    matDialogService: MatDialog
  }
) {
  const { authService, router, matDialogService } = context;

  logger.info('Authentication failed. Redirecting to login route...');

  if (authService.isLoggedIn) {
    authService.logout();
  }

  matDialogService.closeAll();

  if (!router.url.startsWith('/login')) {
    router.navigate(['/login'], {
      state: {
        redirectUrl: router.url.split('?')[0],
      },
    });
  }

  return throwError(() => error);
}

const errorHandlers: Record<number, ErrorHandler> = {
  400: handleError('Bad request'),
  401: handleUnauthorizedError,
  403: handleError('Forbidden'),
  404: handleError('Not found'),
  422: handleError('Unprocessable Entity'),
  500: handleError('server'),
  502: handleError('server'),
  503: handleError('server'),
  504: handleError('server')
};
